import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, ShieldCheck, Menu, Phone, MapPin } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

const navLinks = [
  { to: "/products/huntheal-antivirus-pc", label: "Huntheal Antivirus" },
  { to: "/products", label: "All Plans" },
  { to: "/blog", label: "Blog" },
  { to: "/support", label: "Support" },
];

export const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
      {/* Contact strip */}
      <div className="hidden border-b border-slate-200 bg-slate-950 text-white sm:block">
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-4 px-4 text-[11px] font-medium sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 text-slate-300">
            <MapPin className="h-3 w-3 text-emerald-400" />
            <span>2118 Glenridge Ct, Marietta, GA 30062</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-slate-300 md:inline">
              30-day money-back guarantee · Instant license delivery
            </span>
            <a
              href="tel:+18886554464"
              data-testid="header-phone-link"
              className="flex items-center gap-1.5 font-bold text-emerald-400 hover:text-emerald-300"
            >
              <Phone className="h-3 w-3" />
              +1 (888) 655-4464
            </a>
          </div>
        </div>
      </div>

      <div className="border-b border-slate-200">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="flex items-center gap-2"
          data-testid="brand-logo"
        >
          <span className="flex h-9 w-9 items-center justify-center bg-emerald-500">
            <ShieldCheck className="h-5 w-5 text-slate-900" strokeWidth={2.5} />
          </span>
          <div className="leading-tight">
            <div className="font-display text-lg font-black tracking-tight text-slate-900">
              PROSOFT <span className="text-emerald-600">MARKETPLACE</span>
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              PC Antivirus
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
              end={false}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
            data-testid="open-cart-btn"
            className="relative rounded-sm"
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center bg-emerald-500 px-1 text-[11px] font-bold text-slate-900"
              >
                {count}
              </span>
            )}
          </Button>

          <Button
            onClick={() => navigate("/products")}
            data-testid="header-view-plans-btn"
            className="hidden rounded-sm bg-emerald-500 font-semibold text-slate-900 hover:bg-emerald-400 sm:inline-flex"
          >
            View Plans
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="rounded-sm px-3 py-3 text-sm font-medium text-slate-800 hover:bg-slate-100"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </div>
    </header>
  );
};
